import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MailCheck, Trash2, Users } from "lucide-react";
import { API, authHeaders } from "@/lib/api";

export const NewsletterPanel = ({ onAuthError }) => {
  const [subs, setSubs] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/newsletter/subscribers`, { headers: authHeaders() });
      setSubs(res.data.subscribers);
      setActiveCount(res.data.active_count);
    } catch (err) {
      if (err.response?.status === 401) return onAuthError();
      toast.error("Could not load subscribers");
    } finally {
      setLoading(false);
    }
  }, [onAuthError]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/newsletter/subscribers/${id}`, { headers: authHeaders() });
      setSubs((s) => s.filter((x) => x.id !== id));
      setActiveCount((c) => Math.max(0, c - 1));
      toast.success("Subscriber removed");
    } catch (err) {
      if (err.response?.status === 401) return onAuthError();
      toast.error("Remove failed");
    }
  };

  return (
    <div data-testid="newsletter-panel">
      <div className="bg-slate2 border border-white/10 p-6 mb-8 flex items-center gap-4">
        <Users size={20} className="text-gold" />
        <div>
          <p className="font-serif text-2xl" data-testid="newsletter-count">{activeCount}</p>
          <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Active subscribers</p>
        </div>
        <p className="text-xs text-muted-foreground ml-auto max-w-xs leading-relaxed">
          The weekly digest goes out automatically every Monday morning (IST) with the week's insights.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gold" size={26} /></div>
      ) : subs.length === 0 ? (
        <div className="bg-slate2 border border-white/10 py-20 text-center" data-testid="newsletter-empty">
          <MailCheck size={26} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-sm">No subscribers yet — the signup box is live on the Insights page and footer.</p>
        </div>
      ) : (
        <div className="bg-slate2 border border-white/10" data-testid="newsletter-list">
          {subs.map((s) => (
            <div key={s.id} data-testid={`subscriber-${s.id}`} className="flex items-center justify-between gap-4 px-6 py-4 border-b border-white/5 last:border-b-0">
              <div>
                <p className="text-sm text-foreground">{s.email}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {new Date(s.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  {" · "}
                  <span className={s.active ? "text-emerald-300" : "text-muted-foreground"}>{s.active ? "active" : "unsubscribed"}</span>
                </p>
              </div>
              <button
                data-testid={`subscriber-delete-${s.id}`}
                onClick={() => remove(s.id)}
                className="text-muted-foreground hover:text-red-400 transition-colors"
                aria-label="Remove subscriber"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
